import React from "react";
import { Box, Button, Switch, Typography, Grid } from "@tiny/components";
import SegmentList from "../../components/SegmentList/SegmentList";
import { SegmentListProps } from "../../components/SegmentList/SegmentList.types";
import { useGlobalActiveSegmentTab } from "./GlobalActiveSegmentTab.hooks";
import useBreakpoints from "@tiny/components/src/hooks/useBreakpoints";
import GlobalActiveEngagerSettingsDialog from "./GlobalActiveEngagerSettingsDialog";
import WarningOutline from "@tiny/icons/generated/WarningOutline";

const DESCRIPTION_PARAGRAPHS = [
  "Global Active Segment is a fully managed, inboxing-optimized audience that keeps your list healthy by automatically adjusting based on ISP feedback. It targets your most engaged contacts: created in the last 10 days with source as 'Manual Upload', opened in the last 30 days, or clicked in the last 60 days.",
  "It's automatically included in every campaign — no manual selection needed.",
  "If issues arise at a specific ISP, we temporarily shift sending there to a more engaged segment, then expand back once inboxing recovers.",
  "No action required — we manage everything for you.",
];

function GlobalActiveSegmentTab(
  props: Pick<SegmentListProps, "selected" | "setSelected">
) {
  const { mdUp, lgUp } = useBreakpoints();
  const { isLoading, isEnabled, isToggling, segmentId, handleToggleEnabled } =
    useGlobalActiveSegmentTab();
  const [configureOpen, setConfigureOpen] = React.useState(false);

  const showNotEnabledAlert = !isLoading && (!isEnabled || !segmentId);
  const showSegment = !isLoading && isEnabled && !!segmentId;

  return (
    <>
      <Grid container spacing={lgUp ? 5 : mdUp ? 4 : 2}>
        <Grid item xs={12}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            bgcolor="#f0fdfa"
            borderLeft="4px solid #14b8a6"
            px={5}
            py={4}
            style={{
              borderRadius: "0 8px 8px 0",
              boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              style={{ gap: 16, flex: 1 }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                bgcolor="#ccfbf1"
                style={{
                  borderRadius: "50%",
                  color: "#0d9488",
                  flexShrink: 0,
                  width: 40,
                  height: 40,
                }}
              >
                <svg
                  width="20"
                  height="20"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    clipRule="evenodd"
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  />
                </svg>
              </Box>
              <Box style={{ flex: 1 }}>
                {DESCRIPTION_PARAGRAPHS.map((paragraph, i) => (
                  <Typography
                    key={i}
                    variant="body2"
                    color="grey"
                    tone={900}
                    component="h2"
                    mb={i < DESCRIPTION_PARAGRAPHS.length - 1 ? 1 : 0}
                  >
                    {paragraph}
                  </Typography>
                ))}
              </Box>
            </Box>
            <Box
              ml={3}
              display="flex"
              alignItems="center"
              style={{ flexShrink: 0, gap: 12 }}
            >
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <Typography variant="body2" color="grey" tone={700}>
                  {isEnabled ? "Enabled" : "Disabled"}
                </Typography>
                <Switch
                  size="small"
                  checked={isEnabled}
                  onClick={handleToggleEnabled}
                  disabled={isLoading || isToggling}
                />
              </Box>
              {isEnabled && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => setConfigureOpen(true)}
                >
                  Configure
                </Button>
              )}
            </Box>
          </Box>
        </Grid>
        {isEnabled && (
          <Grid item xs={12}>
            <Box
              display="flex"
              alignItems="flex-start"
              style={{
                gap: 10,
                padding: "10px 14px",
                backgroundColor: "#FFF7ED",
                borderLeft: "3px solid #EA580C",
                borderRadius: 4,
              }}
            >
              <WarningOutline
                style={{
                  color: "#C2410C",
                  width: 16,
                  height: 16,
                  flexShrink: 0,
                  marginTop: 2,
                }}
              />
              <Typography
                variant="body2"
                component="p"
                color="grey"
                tone={900}
                sx={{
                  margin: 0,
                  fontWeight: 550,
                  color: "#7C2D12",
                  lineHeight: "20px",
                }}
              >
                Uploading a large list while Global Active Engagers is enabled
                may result in a substantially higher audience size than expected
                in the campaigns, as recently created users within the
                configured timeframe will be automatically included. Please
                review the potential volume impact before proceeding.
              </Typography>
            </Box>
          </Grid>
        )}
        {showNotEnabledAlert && (
          <Grid item xs={12}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              bgcolor="hsla(168, 20%, 85%, 0.48)"
              borderLeft="4px solid hsla(180, 3%, 26%, 0.07)"
              px={5}
              py={4}
              style={{
                borderRadius: "0 8px 8px 0",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}
            >
              <Box display="flex" alignItems="center" style={{ gap: 16 }}>
                <Box>
                  <Typography
                    variant="body2"
                    component="span"
                    color="grey"
                    tone={900}
                  >
                    Global Active Segment is not yet enabled. Use the toggle
                    above to activate it.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        )}
        <Grid item xs={12}>
          {showSegment && (
            <SegmentList
              pageName="globalActiveSegment"
              segmentIds={[segmentId]}
              aiEnabled={true}
              selected={props.selected}
              setSelected={props.setSelected}
              disablePagination={true}
            />
          )}
        </Grid>
      </Grid>
      <GlobalActiveEngagerSettingsDialog
        open={configureOpen}
        onClose={() => setConfigureOpen(false)}
      />
    </>
  );
}

export default GlobalActiveSegmentTab;
